import React, { useState } from "react";
import A12Y_CONST from "../common/a12yConst";
import PopUpComponent from "./PopUpComponent";
import OneTimeActivationPopup from "./OneTimeActivationPopup";
import PopUpNewDependentConfirmation from "./PopUpNewDependentConfirmation";
import LoaderFullScreenComponent from "./LoaderFullScreenComponent";
import { secureClient } from "../config/axiosClient";
import moment from "moment";

function getInitials(firstName, lastName) {
  const f = firstName ? firstName.charAt(0).toUpperCase() : "";
  const l = lastName ? lastName.charAt(0).toUpperCase() : "";
  return f + l || "?";
}

function StatusBadge({ status, onReactivateClick }) {
  const isActive =
    status === A12Y_CONST.TEXT.ACTIVE ||
    status === A12Y_CONST.TEXT.ACTIVE_UPPER ||
    status === 0;
  return (
    <span
      className={isActive ? "depDash__badge depDash__badge--active" : "depDash__badge depDash__badge--inactive"}
      style={!isActive ? { cursor: "pointer" } : {}}
      onClick={(e) => {
        if (!isActive && onReactivateClick) {
          e.stopPropagation();
          e.preventDefault();
          onReactivateClick(e);
        }
      }}
    >
      <span className="depDash__badge-dot" />
      {isActive ? 'Active' : 'REACTIVATE'}
    </span>
  );
}

function AvatarCircle({ dependent }) {
  const [imgError, setImgError] = useState(false);
  const profilePic = dependent?.profile_pic_url || dependent?.profile_pic;
  const initials = getInitials(dependent?.first_name, dependent?.last_name);

  if (profilePic && !imgError) {
    return (
      <img
        src={profilePic}
        alt={dependent?.nick_name || initials}
        className="depDash__avatar-img"
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div className="depDash__avatar-fallback">
      <span>{initials}</span>
    </div>
  );
}

function MetricPill({ label, count, colorClass, onClick, disabled }) {
  return (
    <button
      className={`depDash__metric depDash__metric--${colorClass}`}
      onClick={!disabled ? onClick : undefined}
      type="button"
      disabled={disabled}
      style={disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
    >
      <span className="depDash__metric-count">{count}</span>
      <span className="depDash__metric-label">{label}</span>
    </button>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  const [copied, setCopied] = useState(false);

  let displayValue = value;
  if (typeof value === 'object' && value !== null) {
    displayValue = value.email || JSON.stringify(value);
  }
  if (!displayValue) displayValue = "—";

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!displayValue || displayValue === "—") return;
    navigator.clipboard.writeText(displayValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="depDash__info-row">
      {Icon && <span className="depDash__info-icon"><Icon /></span>}
      <span className="depDash__info-label">{label}</span>
      <span className="depDash__info-value" title={displayValue}>{displayValue}</span>
      {label === "Email" && displayValue !== "—" && (
        <button
          className={`depDash__copy-btn copyIcon ${copied ? "copied" : ""}`}
          onClick={handleCopy}
          type="button"
          title={copied ? "" : "Copy Email"}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

function EmailCopyButton({ email }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      className={`depDash__copy-btn-email ${copied ? "copied" : ""}`}
      onClick={handleCopy}
      type="button"
      title={copied ? "" : "Copy Email"}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}


// ─── Inline SVG icons ────────────────────────────────────────────────────────
const CaseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="7" width="20" height="14" rx="2" stroke="#F2A950" strokeWidth="2" />
    <path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" stroke="#F2A950" strokeWidth="2" />
    <line x1="12" y1="12" x2="12" y2="16" stroke="#F2A950" strokeWidth="2" strokeLinecap="round" />
    <line x1="10" y1="14" x2="14" y2="14" stroke="#F2A950" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const DateIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="#F2A950" strokeWidth="2" />
    <path d="M12 7v5l3 3" stroke="#F2A950" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CountyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="#F2A950" strokeWidth="2" />
    <circle cx="12" cy="9" r="2.5" stroke="#F2A950" strokeWidth="2" />
  </svg>
);

const AdminIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H11C9.93913 15 8.92172 15.4214 8.17157 16.1716C7.42143 16.9217 7 17.9391 7 19V21" stroke="#F2A950" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="7" r="4" stroke="#F2A950" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const EmailIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 7L12 12L22 7" stroke="#F2A950" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 20H5V9L12 4L19 9V20Z" stroke="#F2A950" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


export default function DependentCard({ dependent, onNavigate, detailsRoute, selectedDependentId, refreshList, isQuotaFull, isQuotaExceeded, shouldDisable, activationFee, excemptAllFee, onStatusError, subscription, currentCount, userSubscriptionAllDetails, billingScreenHandle, checkQuotaAndShowPopup }) {
  const [showPopup, setShowPopup] = useState(false);
  const [showActivationPopup, setShowActivationPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorPopUp, setShowErrorPopUp] = useState(false);
  const [showAccessRestrictedPopup, setShowAccessRestrictedPopup] = useState(false);
  const [showMinDependentPopup, setShowMinDependentPopup] = useState(false);
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);
  const [showTrialReactivateRestrictedPopup, setShowTrialReactivateRestrictedPopup] = useState(false);
  const [showCancelSubscriptionAtWindowEndPopup, setShowCancelSubscriptionAtWindowEndPopup] = useState(false);
  const [localStatus, setLocalStatus] = useState(dependent?.status ?? 1); // assume 0 active, 1 inactive
  const [nextBill, setNextBill] = useState(0);
  const [proratedAmount, setProratedAmount] = useState(0);
  const [activation_fee, setActivationFee] = useState(0);

  const isActive = localStatus === 0;
  const isCardDisabled = shouldDisable || dependent?.contact_advocate;


  const fullName =
    `${dependent?.first_name || ""} ${dependent?.last_name || ""}`.trim() ||
    "Unknown";

  const depStatus = isActive ? 0 : 1;

  const expenseCount = dependent?.metadata?.expense_count ?? dependent?.expense_count ?? 0;
  const incomeCount = dependent?.metadata?.income_count ?? dependent?.income_count ?? 0;
  const assetCount = dependent?.metadata?.asset_count ?? dependent?.asset_count ?? 0;
  const medicineCount = dependent?.metadata?.medicine_count ?? dependent?.medicine_count ?? 0;
  const activityCount = dependent?.metadata?.activity_count ?? dependent?.activity_count ?? 0;
  const documentCount = dependent?.metadata?.doc_count ?? dependent?.doc_count ?? 0;

  const goTo = (route) => { if (onNavigate) onNavigate(dependent, route); };

  const isSelected = selectedDependentId && dependent?.id === selectedDependentId;

  const handleProceed = async (paymentData) => {
    setLoading(true);
    try {
      const body = { status: 0 };
      if (paymentData && !paymentData.useExisting && paymentData.paymentMethod) {
        body.payment_method_id = paymentData.paymentMethod.id;
      }

      const response = await secureClient.put(
        A12Y_CONST.API_ENDPOINT.ADVOCATE_DEPENDENTS + "/" + dependent.id + A12Y_CONST.API_ENDPOINT.ADVOCATE_DEPENDENTS_STATUS,
        body
      );

      if (!response.data.error && response.data.status === A12Y_CONST.API_RESPONSE_STATUS.OK) {
        // Refresh the dependent list to reflect the new status
        if (refreshList) {
          await refreshList();
        }
        setShowActivationPopup(false);
      } else {
        setErrorMessage(response.data.sr?.message || response.data.message || "Activation failed. Please try again.");
        setShowErrorPopUp(true);
      }
    } catch (error) {
      console.error("Activation failed", error);
      setErrorMessage("Activation failed. Please try again.");
      setShowErrorPopUp(true);
    } finally {
      setLoading(false);
    }
  };
  const handleStatusToggle = async (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (!dependent?.access?.can_update) return;
    
    if (userSubscriptionAllDetails?.payment_method?.is_valid === false && dependent?.status === 1) {
      setShowPaymentWarning(true);
      return;
    }
    if ((userSubscriptionAllDetails?.cancel_at_period_end || subscription?.cancel_at_period_end) && userSubscriptionAllDetails?.subscription_details?.subscription_status.toLowerCase() === 'active') {
      setShowCancelSubscriptionAtWindowEndPopup(true);
      return;
    }
    if (isActive) {
      if (currentCount <= 1) {
        setShowMinDependentPopup(true);
        return;
      }
      setLoading(true);
      if (userSubscriptionAllDetails?.subscription_details?.subscription_status?.toLowerCase() !== 'trialing') {
        try {
          const prorationRes = await secureClient.get(A12Y_CONST.API_ENDPOINT.GET_PRORATION, {
            params: { actionType: 'downgrade' }
          });
          if (!prorationRes.data.error && prorationRes.data.status === A12Y_CONST.API_RESPONSE_STATUS.OK) {
            setNextBill(prorationRes.data.sr?.next_bill || 0);
            setProratedAmount(prorationRes.data.sr?.prorated_amount || 0);
            setActivationFee(prorationRes.data.sr?.activation_fee / 100 || 0);
          } else {
            const errMsg = prorationRes.data?.sr?.a12yErr?.message || prorationRes.data?.a12yErr?.message || "Unable to calculate proration!";
            if (onStatusError) onStatusError(errMsg);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.error('Failed to get proration details', err);
          const errMsg = err.response?.data?.sr?.a12yErr?.message || err.response?.data?.a12yErr?.message || "Unable to calculate proration!";
          if (onStatusError) onStatusError(errMsg);
          setLoading(false);
          return;
        }
      }
      setLoading(false);
      setShowPopup(true);
    } else {
      if (checkQuotaAndShowPopup && checkQuotaAndShowPopup()) {
        return;
      }
      
      const isTrialing = userSubscriptionAllDetails?.trial_period === true && 
        (userSubscriptionAllDetails?.status?.toLowerCase() === "trialing" || 
         userSubscriptionAllDetails?.subscription_details?.subscription_status?.toLowerCase() === "trialing");
      // if (isTrialing) {
      //   setShowTrialReactivateRestrictedPopup(true);
      //   return;
      // }
      setLoading(true);
      if(userSubscriptionAllDetails?.subscription_details?.subscription_status?.toLowerCase() !== "trialing"){
          try {
        const prorationRes = await secureClient.get(A12Y_CONST.API_ENDPOINT.GET_PRORATION);
        if (!prorationRes.data.error && prorationRes.data.status === A12Y_CONST.API_RESPONSE_STATUS.OK) {
          setNextBill(prorationRes.data.sr?.next_bill || 0);
          setProratedAmount(prorationRes.data.sr?.prorated_amount || 0);
          setActivationFee(prorationRes.data.sr?.activation_fee / 100 || 0);
        } else {
          const errMsg = prorationRes.data?.sr?.a12yErr?.message || prorationRes.data?.a12yErr?.message || "Unable to calculate proration!";
          if (onStatusError) onStatusError(errMsg);
          setLoading(false);
          return;
        }
      } catch (err) {
       
        const errMsg = err.response?.data?.sr?.a12yErr?.message || err.response?.data?.a12yErr?.message || "Unable to calculate proration!";
        if (onStatusError) onStatusError(errMsg);
        setLoading(false);
        return;
      }
      }
    
      setLoading(false);
      setShowActivationPopup(true);
    }
  };

  const handleCardClick = (e) => {
    if (dependent?.contact_advocate) {
      e.preventDefault();
      e.stopPropagation();
      setShowAccessRestrictedPopup(true);
    }
  };

  return (
    <>
      {loading && !showActivationPopup && <LoaderFullScreenComponent />}
      <div
        className={`depDash__card ${isSelected ? 'depDash__card--selected' : ''} ${!isActive ? 'depDash__card--inactive' : ''}`}
        style={isSelected ? { border: "2px solid #f2a950", cursor: dependent?.contact_advocate ? "pointer" : "default" } : { cursor: dependent?.contact_advocate ? "pointer" : "default" }}
        onClick={handleCardClick}
      >
        {/* {dependent.contact_advocate && <div className="depDash_view_only_inactive_user">contact to your admin</div>} */}
        {/* ── Card header ── */}
        <div className={`depDash__card-header ${isActive && !isCardDisabled ? 'cursorPointer' : ''}`} onClick={() => { if (isActive && !isCardDisabled) goTo(detailsRoute || A12Y_CONST.ROUTE.DEPENDENT_VIEW); }}>
          <div className="depDash__avatar" style={!isActive ? { opacity: 0.5 } : {}}>
            <AvatarCircle dependent={dependent} />
          </div>
          <div className="depDash__card-meta">
            <h3 className="depDash__card-name" style={!isActive ? { opacity: 0.5 } : {}}>{fullName}</h3>
            <div style={{ display: "flex", alignItems: "center", width: "100%", gap: "8px" }}>
              <StatusBadge status={depStatus} onReactivateClick={handleStatusToggle} />
              {dependent?.access?.can_update ?
                <label
                  className="depDash__status-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (dependent?.contact_advocate) {
                      setShowAccessRestrictedPopup(true);
                    }
                  }}
                  style={(!dependent?.access?.can_update) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <input
                    type="checkbox"
                    className="depDash__toggle-input"
                    checked={isActive}
                    // disabled={!dependent?.access?.can_update }
                    onClick={(e) => { e.stopPropagation(); }}
                    onChange={handleStatusToggle}
                  />
                  <span className="depDash__toggle-slider" onClick={(e) => { e.stopPropagation(); }}></span>
                </label> :
                <span className="depDash_view-only-badge">view only</span>
              }
            </div>
          </div>
        </div>

        {/* ── Info rows ── */}
        <div className="depDash__card-body" style={!isActive ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
          <InfoRow icon={DateIcon} label="Date Appointed" value={dependent?.court_date || dependent?.appointment_date} />
          <InfoRow icon={CountyIcon} label="County" value={dependent?.county?.[0]?.county || dependent?.dependent_addresses?.[0]?.county || dependent?.address?.county} />
          <InfoRow icon={CaseIcon} label="Case number" value={dependent?.case_file_number || dependent?.case_file_num} />
          {!dependent?.access?.can_update && dependent?.advocate &&
            <>
              <InfoRow icon={AdminIcon} label="Admin" value={`${dependent?.advocate?.first_name || ""} ${dependent?.advocate?.last_name || ""}`} />
              <InfoRow icon={EmailIcon} label="Admin Email" value={dependent?.advocate?.email || ""} />
            </>
          }
        </div>

        {/* ── Metrics 2×2 grid ── */}
        <div className="depDash__card-metrics" style={!isActive || isCardDisabled ? { pointerEvents: 'none' } : {}}>
          {/* Row 1: primary metrics */}
          <div className="depDash__metrics-row depDash__metrics-row--sm">
            <MetricPill label="Activity" count={activityCount} colorClass="green" onClick={() => goTo(A12Y_CONST.ROUTE.ACTIVITIES)} disabled={isCardDisabled} />
            <MetricPill label="Expenses" count={expenseCount} colorClass="orange" onClick={() => goTo(A12Y_CONST.ROUTE.EXPENSES)} disabled={isCardDisabled} />
          </div>
          {/* Row 2: secondary metrics */}
          <div className="depDash__metrics-row depDash__metrics-row--sm">
            <MetricPill label="Income" count={incomeCount} colorClass="teal" onClick={() => goTo(A12Y_CONST.ROUTE.INCOMES)} disabled={isCardDisabled} />
            <MetricPill label="Documents" count={documentCount} colorClass="purple" onClick={() => goTo(A12Y_CONST.ROUTE.DOCUMENTS)} disabled={isCardDisabled} />
          </div>
          <div className="depDash__metrics-row depDash__metrics-row--sm">
            <MetricPill label="Inventory" count={assetCount} colorClass="navy" onClick={() => goTo(A12Y_CONST.ROUTE.INVENTORY)} disabled={isCardDisabled} />
            <MetricPill label="Medical" count={medicineCount} colorClass="rose" onClick={() => goTo(A12Y_CONST.ROUTE.MEDICAL)} disabled={isCardDisabled} />
          </div>
        </div>

        {/* ── Quick actions ── */}
        <div
          className="depDash__card-actions"
          onClick={(e) => {
            if (dependent?.contact_advocate) {
              e.stopPropagation();
              setShowAccessRestrictedPopup(true);
            }
          }}
        >
          {/* <button className="depDash__action-btn depDash__action-btn--primary"   onClick={() => goTo(A12Y_CONST.ROUTE.DASHBOARD_VIEW)} type="button" disabled={isCardDisabled || !isActive} >View Dashboard</button> */}
          <button className="depDash__action-btn depDash__action-btn--secondary" onClick={() => goTo(A12Y_CONST.ROUTE.DEPENDENT_VIEW)} type="button" disabled={isCardDisabled || !isActive}>Profile</button>
        </div>
      </div>

      {/* Access Restricted Popup Modal */}
      {showAccessRestrictedPopup && (
        <div className="popTop" onClick={() => setShowAccessRestrictedPopup(false)}>
          <div className="popContainer popMaxWidth" onClick={(e) => e.stopPropagation()}>
            <div className="popMaster">
              <div className="popUpMainBg fullWidth">

                {/* Visual Icon from DOCUMENT_UNABLE_TO_REMOVE */}
                <div className="cancelIcon fullWidth marginTopBottom20">
                  <svg xmlns="http://www.w3.org/2000/svg" width="58" height="58" viewBox="0 0 58 58" fill="none">
                    <path d="M29 5.8C16.1869 5.8 5.8 16.1878 5.8 29C5.8 41.8122 16.1869 52.2 29 52.2C41.8131 52.2 52.2 41.8122 52.2 29C52.2 16.1878 41.8131 5.8 29 5.8ZM0 29C0 12.9833 12.9839 0 29 0C45.0161 0 58 12.9833 58 29C58 45.0167 45.0161 58 29 58C12.9839 58 0 45.0167 0 29Z" fill="#D83933" />
                    <path d="M29.0001 34.7996C27.3984 34.7996 26.1001 33.5004 26.1001 31.8996V14.4996C26.1001 12.8988 27.3984 11.5996 29.0001 11.5996C30.6018 11.5996 31.9001 12.8988 31.9001 14.4996V31.8996C31.9001 33.5004 30.6018 34.7996 29.0001 34.7996Z" fill="#D83933" />
                    <path d="M24.6499 42.0512C24.6499 39.65 26.5975 37.7012 28.9999 37.7012C31.4023 37.7012 33.3499 39.65 33.3499 42.0512C33.3499 44.4524 31.4023 46.4012 28.9999 46.4012C26.5975 46.4012 24.6499 44.4524 24.6499 42.0512Z" fill="#D83933" />
                  </svg>
                </div>

                {/* Message Header */}
                <div className="areYouText areYouDmFont">
                  Access Restricted
                </div>

                {/* Subtitle / More Info and Admin Details Box */}
                <div className="useInfo marginTop10">
                  <div >Please contact admin</div>
                  <div className="depDash__restricted-admin-box">
                    <div className="depDash__restricted-admin-row">
                      {/* <div className="depDash__restricted-label-title">Admin Name</div> */}
                      <div className="depDash__restricted-label-value">
                        {`${dependent?.advocate?.first_name || ""} ${dependent?.advocate?.last_name || ""}`.trim() || "System Admin"}
                      </div>
                    </div>
                    <div className="depDash__restricted-email-row">
                      <div>
                        {/* <div className="depDash__restricted-label-title">Admin Email</div> */}
                        <div className="depDash__restricted-label-value-email">
                          {dependent?.advocate?.email || "admin@advocord.com"}
                        </div>
                      </div>
                      {dependent?.advocate?.email && (
                        <EmailCopyButton email={dependent.advocate.email} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Close Button */}
                <div className="fullWidth texAlingCener df jcc aic">
                  <div
                    className="popUpNoButton"
                    onClick={() => setShowAccessRestrictedPopup(false)}
                  >
                    Close
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Minimum Dependent Required Popup */}
      {showMinDependentPopup && (
        <div className="popTop" onClick={() => setShowMinDependentPopup(false)}>
          <div className="popContainer popMaxWidth" onClick={(e) => e.stopPropagation()}>
            <div className="popMaster">
              <div className="popUpMainBg fullWidth">

                {/* Warning Icon */}
                <div className="cancelIcon fullWidth marginTopBottom20">
                  <svg xmlns="http://www.w3.org/2000/svg" width="58" height="58" viewBox="0 0 58 58" fill="none">
                    <path d="M29 5.8C16.1869 5.8 5.8 16.1878 5.8 29C5.8 41.8122 16.1869 52.2 29 52.2C41.8131 52.2 52.2 41.8122 52.2 29C52.2 16.1878 41.8131 5.8 29 5.8ZM0 29C0 12.9833 12.9839 0 29 0C45.0161 0 58 12.9833 58 29C58 45.0167 45.0161 58 29 58C12.9839 58 0 45.0167 0 29Z" fill="#D83933" />
                    <path d="M29.0001 34.7996C27.3984 34.7996 26.1001 33.5004 26.1001 31.8996V14.4996C26.1001 12.8988 27.3984 11.5996 29.0001 11.5996C30.6018 11.5996 31.9001 12.8988 31.9001 14.4996V31.8996C31.9001 33.5004 30.6018 34.7996 29.0001 34.7996Z" fill="#D83933" />
                    <path d="M24.6499 42.0512C24.6499 39.65 26.5975 37.7012 28.9999 37.7012C31.4023 37.7012 33.3499 39.65 33.3499 42.0512C33.3499 44.4524 31.4023 46.4012 28.9999 46.4012C26.5975 46.4012 24.6499 44.4524 24.6499 42.0512Z" fill="#D83933" />
                  </svg>
                </div>

                {/* Message Header */}
                <div className="areYouText areYouDmFont">
                  Action Not Allowed
                </div>

                {/* Subtitle */}
                <div className="useInfo marginTop10">
                  <div>A minimum of one dependent is required.</div>
                </div>

                {/* Close Button */}
                <div className="fullWidth texAlingCener df jcc aic marginTop20">
                  <div
                    className="popUpNoButton"
                    onClick={() => setShowMinDependentPopup(false)}
                  >
                    Close
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      <PopUpNewDependentConfirmation
        activationFee={activation_fee}
        popUpStatus={showPopup}
        onClose={() => setShowPopup(false)}
        onConfirm={async () => {
          setLoading(true);
          try {
            const response = await secureClient.put(A12Y_CONST.API_ENDPOINT.ADVOCATE_DEPENDENTS + "/" + dependent.id + A12Y_CONST.API_ENDPOINT.ADVOCATE_DEPENDENTS_STATUS, { status: 1 });
            if (!response.data.error && response.data.status === A12Y_CONST.API_RESPONSE_STATUS.OK) {
              setLocalStatus(1); // set to inactive
              refreshList && refreshList();
            } else {
              const msg = response.data?.sr?.a12yErr?.message || response.data?.sr?.message || response.data?.message || "Unable to update dependent status!";
              if (onStatusError) onStatusError(msg);
            }
          } catch (error) {
            console.error("Failed to update status", error);
            if (onStatusError) onStatusError("Unable to update dependent status!");
          }
          setLoading(false);
          setShowPopup(false);
        }}
        popUpType="DOWNGRADE_CONFIRMATION"
        subscription={subscription}
        currentCount={currentCount}
        proratedAmount={proratedAmount}
        nextBill={nextBill}
      />

      <OneTimeActivationPopup
        isOpen={showActivationPopup}
        dependentName={fullName}
        activationFee={activation_fee}
        excemptAllFee={excemptAllFee}
        onClose={() => setShowActivationPopup(false)}
        onProceed={handleProceed}
        loading={loading}
        subscription={subscription}
        currentCount={currentCount}
        proratedAmount={proratedAmount}
        nextBill={nextBill}
      />

      <PopUpComponent
        popUpStatus={showErrorPopUp}
        title="Activation Error"
        message={errorMessage}
        buttonDeleteTxt="OK"
        buttonCancelTxt="CANCEL"
        handleDeleteButton={() => setShowErrorPopUp(false)}
        hidePopUp={() => setShowErrorPopUp(false)}
      />

      <PopUpComponent
        popUpStatus={showPaymentWarning}
        title="Invalid Payment Method"
        message="Your current payment information is not correct. Please add a valid payment method to continue."
        buttonDeleteTxt="Continue"
        buttonCancelTxt="Cancel"
        handleDeleteButton={() => {
          setShowPaymentWarning(false);
          if (billingScreenHandle) {
            billingScreenHandle();
          } else {
            goTo(A12Y_CONST.ROUTE.ADVOCATE_PROFILE_MY_ACCOUNT);
          }
        }}
        hidePopUp={() => setShowPaymentWarning(false)}
      />

      <PopUpComponent
        popUpStatus={showTrialReactivateRestrictedPopup}
        title="Subscribe Now"
        message="Account reactivation is not available during the trial period. You can reactivate accounts by upgrading to a paid subscription."
        buttonDeleteTxt="Continue"
        buttonCancelTxt="Cancel"
        handleDeleteButton={() => {
          setShowTrialReactivateRestrictedPopup(false);
          if (billingScreenHandle) {
            billingScreenHandle(true);
          } else {
            goTo(A12Y_CONST.ROUTE.ADVOCATE_PROFILE_MY_ACCOUNT);
          }
        }}
        hidePopUp={() => setShowTrialReactivateRestrictedPopup(false)}
      />

      <PopUpComponent
        popUpStatus={showCancelSubscriptionAtWindowEndPopup}
        title="Cancel Subscription"
        message={`Your subscription will remain active until ${moment(subscription?.subscription_details?.renewal_date).format('MMMM D, YYYY')}. After this date, it will not renew and your access will end.`}
        buttonDeleteTxt="Continue"
        buttonCancelTxt="Cancel"
        handleDeleteButton={() => {
          setShowCancelSubscriptionAtWindowEndPopup(false);
          if (billingScreenHandle) {
            billingScreenHandle(true);
          }
        }}
        hidePopUp={() => setShowCancelSubscriptionAtWindowEndPopup(false)}
      />
    </>
  );
}